/**
 * R-131-RAG-A — Firestore vector search + in-memory fallback.
 *
 * Native Firestore `findNearest` is wired when the SDK exposes it; until
 * then we fetch tenant-scoped chunks and rank in-memory (fine for early
 * corpus sizes). Per-tenant isolation: users/{uid}/documents/.../chunks.
 */
import {
  collection,
  collectionGroup,
  getDocs,
  query,
  where,
  type Firestore,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { cosineSimilarity } from '../embeddings/cosine';
import type { DocumentChunk, VectorChunkHit } from './types';

export const DEFAULT_VECTOR_TOP_K = 20;

/** Rank in-memory chunks by cosine similarity to the query vector. */
export function findNearestChunksInMemory(
  chunks: readonly DocumentChunk[],
  queryVector: readonly number[],
  limit = DEFAULT_VECTOR_TOP_K,
): VectorChunkHit[] {
  if (queryVector.length === 0) return [];

  const hits: VectorChunkHit[] = [];
  for (const chunk of chunks) {
    const embedding = chunk.vectorEmbedding;
    // R-131-RAG-D — keep serving prior vectors while doc.embeddingsReady is false.
    if (!embedding?.length) continue;
    const similarity = cosineSimilarity(queryVector, embedding);
    if (similarity <= 0) continue;
    hits.push({ chunk, similarity });
  }

  hits.sort((a, b) => b.similarity - a.similarity);
  return hits.slice(0, limit);
}

/** Aggregate chunk-level vector hits to best similarity per parent doc. */
export function aggregateVectorHitsByDoc(
  hits: readonly VectorChunkHit[],
): Map<string, { similarity: number; chunk: DocumentChunk }> {
  const byDoc = new Map<string, { similarity: number; chunk: DocumentChunk }>();
  for (const hit of hits) {
    const docId = hit.chunk.docId;
    const prev = byDoc.get(docId);
    if (!prev || hit.similarity > prev.similarity) {
      byDoc.set(docId, { similarity: hit.similarity, chunk: hit.chunk });
    }
  }
  return byDoc;
}

export interface FindNearestChunksArgs {
  uid: string;
  queryVector: readonly number[];
  /** Restrict to these doc ids when the caller already scoped the corpus. */
  docIds?: readonly string[];
  limit?: number;
  /** Injected chunks bypass Firestore (tests + panel-local corpus). */
  chunks?: readonly DocumentChunk[];
  firestore?: Firestore;
}

/**
 * Top-K vector retrieval. Prefers caller-supplied chunks; otherwise loads
 * from Firestore subcollections and ranks in-memory.
 */
export async function findNearestChunks(args: FindNearestChunksArgs): Promise<VectorChunkHit[]> {
  const limit = args.limit ?? DEFAULT_VECTOR_TOP_K;
  if (args.queryVector.length === 0) return [];

  if (args.chunks?.length) {
    const scoped = args.docIds?.length
      ? args.chunks.filter(c => args.docIds!.includes(c.docId))
      : args.chunks;
    return findNearestChunksInMemory(scoped, args.queryVector, limit);
  }

  const native = await tryFindNearestNative(args);
  if (native) return native;

  const fetched = await fetchTenantChunks(args.uid, args.docIds, args.firestore ?? db);
  return findNearestChunksInMemory(fetched, args.queryVector, limit);
}

/** Build synthetic single-chunk entries from whole-doc embeddings. */
export function chunksFromSiteDocs(
  docs: readonly { id: string; textContent?: string; vectorEmbedding?: number[]; embeddingsReady?: boolean }[],
  userId?: string,
): DocumentChunk[] {
  const out: DocumentChunk[] = [];
  for (const doc of docs) {
    if (!doc.vectorEmbedding?.length) continue;
    out.push({
      id: `${doc.id}__whole`,
      docId: doc.id,
      userId,
      text: doc.textContent ?? '',
      vectorEmbedding: doc.vectorEmbedding,
      embeddingsReady: doc.embeddingsReady ?? true,
      chunkRef: { docId: doc.id, chunkId: `${doc.id}__whole` },
    });
  }
  return out;
}

async function fetchTenantChunks(
  uid: string,
  docIds: readonly string[] | undefined,
  firestore: Firestore,
): Promise<DocumentChunk[]> {
  if (docIds?.length) {
    const nested = await Promise.all(
      docIds.map(docId => fetchDocChunks(firestore, uid, docId)),
    );
    return nested.flat();
  }

  // Collection-group sweep scoped by userId field on each chunk doc.
  const q = query(
    collectionGroup(firestore, 'chunks'),
    where('userId', '==', uid),
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...(d.data() as Record<string, unknown>) } as DocumentChunk));
}

async function fetchDocChunks(
  firestore: Firestore,
  uid: string,
  docId: string,
): Promise<DocumentChunk[]> {
  const snap = await getDocs(collection(firestore, `users/${uid}/documents/${docId}/chunks`));
  return snap.docs.map(d => ({ id: d.id, docId, ...(d.data() as Record<string, unknown>) } as DocumentChunk));
}

/**
 * Attempt native Firestore findNearest when the installed SDK exposes it.
 * Returns null so callers fall back to fetch + in-memory ranking.
 */
async function tryFindNearestNative(args: FindNearestChunksArgs): Promise<VectorChunkHit[] | null> {
  const firestoreMod = await import('firebase/firestore') as Record<string, unknown>;
  const findNearestFn = firestoreMod.findNearest as
    | ((...params: unknown[]) => unknown)
    | undefined;
  const vectorFn = firestoreMod.vector as ((values: number[]) => unknown) | undefined;
  if (typeof findNearestFn !== 'function' || typeof vectorFn !== 'function') return null;

  try {
    const firestore = args.firestore ?? db;
    const limit = args.limit ?? DEFAULT_VECTOR_TOP_K;
    const base = query(
      collectionGroup(firestore, 'chunks'),
      where('userId', '==', args.uid),
    );
    const nearestQuery = findNearestFn(
      base,
      'vectorEmbedding',
      vectorFn([...args.queryVector]),
      { limit, distanceMeasure: 'COSINE' },
    );
    const snap = await getDocs(nearestQuery as ReturnType<typeof query>);
    return snap.docs.map(d => {
      const chunk = { id: d.id, ...(d.data() as Record<string, unknown>) } as DocumentChunk;
      const distance = (d.data() as { vectorDistance?: number }).vectorDistance ?? 1;
      return { chunk, similarity: Math.max(0, 1 - distance) };
    });
  } catch (err) {
    console.warn('[vectorRetrieval] findNearest unavailable, using in-memory fallback:', (err as Error).message);
    return null;
  }
}
